/**
 * Learning.tsx — the Manager Metrics Dashboard "compliance & engagement" page (MD-F29..F37). Reads
 * the real learning record: mandatory-training completion and the overdue count (MD-F29/F30), the
 * knowledge-check pass rate (MD-F31), and the self-assessed competency distribution (MD-F35). Every
 * figure is aggregate and names nobody; we record which links were opened, never how long anyone spent
 * or what they searched for.
 *
 * TRIMMED TO CHARTS (this pass). Russell asked for "WAY simpler, just charts", so the page now ends at
 * the competency distribution: the two most-opened-resources and neglected-content lists were REMOVED
 * FROM THE VIEW, not from the API (the /learning route still computes and returns them — reversible via
 * git). What remains is the compliance grid, the training segmented bar and the competency chart+table.
 */
import { useLoaderData } from 'react-router';
import type { LearningResponse, PageState } from './api';
import { SignedOut } from '../admin/shared';
import { NotPermitted } from '../admin/overlay';
import { AsOfNote, MetricCard, SuppressedNote, periodLabel } from './shared';
import { BarChart, SegmentedBar } from './charts';

export function Learning() {
  const data = useLoaderData() as PageState<LearningResponse>;
  if (data.state === 'signedOut')
    return <SignedOut title="Management dashboard" returnTo="/dashboard/learning" />;
  if (data.state === 'forbidden') return <NotPermitted what="Management dashboard" />;

  const { passRate, competency, trainingCompletion, filter, asOf } = data.data;
  const window = periodLabel(filter.windowDays);
  const { counts, completion, overdueAfterDays } = trainingCompletion;

  return (
    <div>
      <h2>Compliance &amp; engagement</h2>
      {/* The period belongs on the page AND beside each figure (MD-F24). This banner is the default and
          not a blanket claim: three things here are measured over something else — the compliance
          position and the competency table are where we stand NOW, and neglected content keeps the
          server's own longer horizon — so each of those states its own period where it is rendered. A
          banner that claimed the period for all of them would be the same defect in reverse. */}
      <p className="tb-filter-live" aria-live="polite">
        Showing the {window}, except where a figure states its own period. Every figure is an
        aggregate — it names nobody. We record which links were opened, never how long anyone spent
        or what they searched for.
      </p>
      {/* Team scope (0049): when a team is selected in the filter bar, the mandatory-training completion
          and overdue figures below are narrowed to that team's roster — still aggregates, still
          cohort-suppressed, so a team below the floor reports nothing. The knowledge-check pass rate and
          the competency distribution are NOT team-scoped and stay platform/department-wide; this note
          says so rather than letting a reader take every figure on the page as the team's. The team is
          named by the selector above; if the id in the URL is one you may not scope to, the server
          silently ignores it and this note does not appear. */}
      {filter.teamId != null && (
        <p className="tb-filter-live" aria-live="polite">
          Scoped to the selected team for the mandatory-training figures below. The knowledge-check
          pass rate and the competency distribution remain platform- or department-wide.
        </p>
      )}
      <AsOfNote asOf={asOf} />

      <div className="tb-dash-grid">
        {/* MD-F29 — mandatory-training completion. Deliberately NOT window-filtered: a compliance
            position is where we stand now, measured against the governed overdue horizon, so the note
            says so rather than letting the page's period be read into it. Both figures render their
            own `detail` sentence when withheld — the reasons here ('too-few', 'headcount-not-recorded')
            are not AwaitingDataCard's set, and that card would explain the wrong thing. */}
        <MetricCard
          label="Mandatory training complete"
          figure={completion.available ? `${completion.pct}%` : '—'}
          note={
            completion.available
              ? `${completion.complete.toLocaleString()} of a recorded headcount of ${completion.headcount.toLocaleString()}. Current position, not the ${window}.`
              : completion.detail
          }
        />
        <MetricCard
          label="Overdue on training"
          figure={counts.available ? counts.overdue.toLocaleString() : '—'}
          note={
            counts.available
              ? `Of ${counts.people.toLocaleString()} people: ${counts.complete.toLocaleString()} complete, ${counts.inProgress.toLocaleString()} in progress, ${counts.notStarted.toLocaleString()} not started. Overdue after ${overdueAfterDays} days — the position now, not the ${window}.`
              : counts.detail
          }
        />
        <MetricCard
          label="Knowledge-check pass rate"
          figure={passRate.rate != null ? `${Math.round(passRate.rate * 100)}%` : 'Too few'}
          note={
            passRate.attempters.available
              ? `${passRate.attempters.count.toLocaleString()} distinct people attempted, ${window}.`
              : 'Too few people have attempted a check to report a rate without identifying someone.'
          }
        />
      </div>

      {/* The three training states partition the cohort exactly (complete + in progress + not started
          = people), so a segmented bar is honest here where a stacked chart of subsets would not be.
          OVERDUE is a SUBSET spanning in-progress and not-started, not a fourth state — it is called
          out below the bar as a number, never drawn as a segment, or the bar would exceed the
          population. Only rendered when the counts are available; when withheld the card above already
          shows the engine's own `detail` sentence, and a bar of a suppressed cohort would be the leak
          the whole page guards against. */}
      {counts.available && (
        <>
          <SegmentedBar
            total={counts.people}
            segments={[
              { label: 'Complete', count: counts.complete, color: 'var(--accent)' },
              { label: 'In progress', count: counts.inProgress, color: 'var(--accent-strong)' },
              { label: 'Not started', count: counts.notStarted, color: 'var(--accent-subtle)' },
            ]}
            legendNote={
              <>
                Of {counts.people.toLocaleString()} people. {counts.overdue.toLocaleString()} of
                them {counts.overdue === 1 ? 'is' : 'are'} overdue (past {overdueAfterDays} days) —
                a subset of those in progress or not started, so it is not a separate band. The
                current position, not the {window}.
              </>
            }
          />
        </>
      )}

      <h3>Self-assessed competency</h3>
      {/* NOT window-filtered, and it has to say so (MD-F24). The engine takes no windowDays — it reads
          each person's CURRENT declared level, so this table is byte-identical for ?period=7, 30 and 180.
          A table that sits under a "showing the last 30 days" banner without that sentence tells a
          manager these are the levels declared in the last month, which is a different and much smaller
          fact; and a reader who moves the selector and watches this not move reads it as broken. */}
      <p className="tb-muted" style={{ maxWidth: '68ch' }}>
        Where colleagues stand today, not what changed in the {window}: each person&rsquo;s current
        self-declared level, so this table does not move when you change the reporting period. The
        platform verifies none of it.
      </p>
      {/* No separate empty state: the server sends every level when it sends any, and an empty
          `levels` only ever accompanies suppressed === true — which includes the nobody-has-assessed
          case. One branch, so there is no unreachable copy claiming to describe a third state. */}
      {competency.suppressed ? (
        <SuppressedNote>
          Too few people have recorded a self-assessment to show a distribution without identifying
          someone.
        </SuppressedNote>
      ) : (
        <>
          {/* Same gate as the table (competency.suppressed): the chart only exists when the whole
              distribution clears the floor. Per-LEVEL cells can still be individually suppressed, and
              those pass value:null so the bar becomes "Too few" rather than a zero — MD-F06 suppresses
              a three-person level even inside a large distribution. Level order is the server's, never
              re-sorted. */}
          <BarChart
            unit="people at each level"
            data={competency.levels.map((l) => ({
              label: l.level,
              value: l.count.available ? l.count.count : null,
            }))}
          />
          <table className="tb-dash-table">
            <thead>
              <tr>
                <th scope="col">Level (self-assessed)</th>
                <th scope="col" className="tb-num">
                  People
                </th>
              </tr>
            </thead>
            <tbody>
              {competency.levels.map((l) => (
                <tr key={l.level}>
                  <td>{l.level}</td>
                  {/* Per-cell suppression (MD-F06): a cell below min_cohort_for_aggregate says so
                    rather than printing a number that would identify a small team. Same wording as
                    the pass-rate card above, so one convention covers both. */}
                  <td className="tb-num">
                    {l.count.available ? l.count.count.toLocaleString() : 'Too few'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
